import React, { useEffect, useState } from "react";
import { Drawer, Table, Tag, Input, Button, Select, Dropdown, Menu } from "antd";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import { RiArrowDownSLine, RiEyeLine, RiCalendarEventLine } from "react-icons/ri";
import { getHmoCustomers, extendHmoExpiry } from "../../../redux/actions";
import { dashboardRoute } from "../../../constants";
import ModalExtendExpiry from "./ModalExtendExpiry";

const { Search } = Input;
const { Option } = Select;

const ModalHmoCustomers = ({ visible, onClose, hmo, onExtendExpiry }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [userToExtend, setUserToExtend] = useState(null);
  const [extendLoading, setExtendLoading] = useState(false);

  const parsePaginatedData = (res) => {
    let list = [];
    let current = 1;
    let pageSize = pagination.pageSize || 15;
    let total = 0;

    if (!res) return { list, current, pageSize, total };

    // Structure 1: { status: 'success', data: { data: [...], current_page: 1, total: 10 } }
    if (res.data && Array.isArray(res.data.data)) {
      list = res.data.data;
      current = res.data.current_page || 1;
      pageSize = res.data.per_page || pageSize;
      total = res.data.total !== undefined ? res.data.total : list.length;
    }
    // Structure 2: { data: [...], current_page: 1, total: 10 }
    else if (Array.isArray(res.data)) {
      list = res.data;
      current = res.current_page || 1;
      pageSize = res.per_page || pageSize;
      total = res.total !== undefined ? res.total : list.length;
    }
    // Structure 3: Direct array [...]
    else if (Array.isArray(res)) {
      list = res;
      total = res.length;
    }

    return { list, current, pageSize, total };
  };

  const fetchCustomers = async (page = 1, searchQuery = search, status = statusFilter) => {
    if (!hmo) return;
    setLoading(true);
    let q = `page=${page}&perPage=${pagination.pageSize}`;
    if (searchQuery) q += `&search=${encodeURIComponent(searchQuery)}`;
    if (status) q += `&status=${status}`;

    const res = await dispatch(getHmoCustomers(hmo.id, q));
    setLoading(false);
    
    const { list, current, pageSize, total } = parsePaginatedData(res);
    setCustomers(list);
    setPagination({
      current,
      pageSize,
      total,
    });
  };

  useEffect(() => {
    if (visible && hmo) {
      setSearch("");
      setStatusFilter("");
      fetchCustomers(1, "", "");
    }
  }, [visible, hmo]);

  const handleSearch = (value) => {
    setSearch(value);
    fetchCustomers(1, value, statusFilter);
  };

  const columns = [
    {
      title: "Customer Name",
      dataIndex: "name",
      key: "name",
      width: 170,
      render: (text, record) => (
        <Link
          to={`${dashboardRoute.admin.userProfile.link}/${record.id}`}
          style={{ fontWeight: 600, color: "#108ee9" }}
        >
          {text}
        </Link>
      ),
    },
    {
      title: "Contact",
      key: "contact",
      width: 190,
      render: (_, record) => (
        <div>
          <div>{record.email}</div>
          <div style={{ fontSize: "12px", color: "#888" }}>{record.phone}</div>
        </div>
      ),
    },
    {
      title: "HMO Number",
      dataIndex: "hmo_number",
      key: "hmo_number",
      width: 130,
      render: (text) => text || "—",
    },
    {
      title: "Status",
      dataIndex: "hmo_status",
      key: "hmo_status",
      width: 130,
      render: (status, record) => {
        if (status === "approved") {
          const isExpired =
            record.hmo_expires_at &&
            moment(record.hmo_expires_at).isBefore(moment().startOf("day"));
          if (isExpired) {
            return <Tag color="error">Expired</Tag>;
          }
          return <Tag color="success">Approved</Tag>;
        }
        if (status === "pending") return <Tag color="warning">Pending Approval</Tag>;
        if (status === "rejected") return <Tag color="error">Rejected</Tag>;
        return <Tag>None</Tag>;
      },
    },
    {
      title: "Expiration Date",
      dataIndex: "hmo_expires_at",
      key: "hmo_expires_at",
      width: 140,
      render: (date) => (date ? moment(date).format("DD MMM, YYYY") : "—"),
    },
    {
      title: "Action",
      key: "action",
      width: 110,
      fixed: "right",
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item
              key="extend"
              icon={<RiCalendarEventLine style={{ verticalAlign: "middle" }} />}
              onClick={() => setUserToExtend(record)}
            >
              Extend Expiry Date
            </Menu.Item>
            <Menu.Item
              key="profile"
              icon={<RiEyeLine style={{ verticalAlign: "middle" }} />}
            >
              <Link to={`${dashboardRoute.admin.userProfile.link}/${record.id}`}>
                View Profile
              </Link>
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
            <Button size="small">
              Actions <RiArrowDownSLine style={{ marginLeft: 2 }} />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const handleExtendSubmit = async (values) => {
    if (!userToExtend) return;
    setExtendLoading(true);
    const res = await dispatch(extendHmoExpiry(userToExtend.id, values));
    setExtendLoading(false);
    if (res && res.success) {
      setUserToExtend(null);
      fetchCustomers(pagination.current);
      if (onExtendExpiry) onExtendExpiry();
    }
  };

  return (
    <Drawer
      title={`Customers Enrolled in: ${hmo ? hmo.name : ""}`}
      width={780}
      visible={visible}
      onClose={onClose}
      destroyOnClose
    >
      <div style={{ marginBottom: "16px", display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", gap: "10px", flex: 1 }}>
          <Search
            placeholder="Search by name, email, phone, HMO no."
            allowClear
            onSearch={handleSearch}
            style={{ width: 280 }}
          />
          <Select
            placeholder="Filter by Status"
            allowClear
            style={{ width: 170 }}
            value={statusFilter || undefined}
            onChange={(val) => {
              setStatusFilter(val || "");
              fetchCustomers(1, search, val || "");
            }}
          >
            <Option value="all_approved">All Approved</Option>
            <Option value="approved">Active (Not Expired)</Option>
            <Option value="expired">Expired</Option>
            <Option value="pending">Pending Approval</Option>
            <Option value="rejected">Rejected</Option>
          </Select>
        </div>
        <Button onClick={() => fetchCustomers(pagination.current)}>Refresh</Button>
      </div>

      <Table
        columns={columns}
        dataSource={customers}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          onChange: (page) => fetchCustomers(page),
        }}
        size="middle"
        scroll={{ x: 750 }}
      />

      <ModalExtendExpiry
        visible={!!userToExtend}
        user={userToExtend}
        loading={extendLoading}
        onClose={() => setUserToExtend(null)}
        onSubmit={handleExtendSubmit}
      />
    </Drawer>
  );
};

export default ModalHmoCustomers;
